import { useState } from "react";
import { ChartOfAccountsTemplateStartingPoint } from "@common/constants/domains/accounting/chartOfAccountsTemplateStartingPoint";
import { ChartOfAccountsTemplateHeader } from "@common/schemas/chart-of-accounts-template.schema";
import { TypedChartOfAccountsTemplateUpload } from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { findApiErrorMessage } from "@api/apiError";
import { useCreateChartOfAccountsTemplateMutation } from "@api/coaTemplate.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { RequiredAccountsMappingDrawer } from "../roles/RequiredAccountsMappingDrawer";
import {
    assignRole,
    dropUnassignedCreatedAccounts,
} from "../roles/requiredAccountsMapping";
import {
    addCreatedRoleAccount,
    buildInitialRoleAccounts,
    buildUploadedTemplateAccountsPayload,
} from "./uploadedTemplateAccounts";

interface Props {
    chart: TypedChartOfAccountsTemplateUpload;
    header: ChartOfAccountsTemplateHeader;
    isDiscardPromptOpen: boolean;
    onClose: () => void;
    onCreated: (template: ChartOfAccountsTemplate) => void;
}

export const ChartOfAccountsTemplateRoleSelectionStep = ({
    chart,
    header,
    isDiscardPromptOpen,
    onClose,
    onCreated,
}: Props) => {
    const { error: errorToast } = useToaster();
    const createTemplate = useCreateChartOfAccountsTemplateMutation();
    const [accounts, setAccounts] = useState(() =>
        buildInitialRoleAccounts({
            chart,
            entityLegalType: header.entityLegalType,
        }),
    );

    const handleContinue = () =>
        createTemplate
            .mutateAsync({
                ...header,
                startingPoint: ChartOfAccountsTemplateStartingPoint.importCoa,
                rows: buildUploadedTemplateAccountsPayload({
                    chart,
                    accounts: dropUnassignedCreatedAccounts(accounts),
                }),
            })
            .then(onCreated, (error: Error) => {
                if (findApiErrorMessage(error) === null) {
                    errorToast("Failed to create template");
                }
            });

    return (
        <RequiredAccountsMappingDrawer
            open
            accounts={accounts}
            entityLegalType={header.entityLegalType}
            confirmLabel="Continue"
            isConfirming={createTemplate.isPending}
            isDiscardPromptOpen={isDiscardPromptOpen}
            onAssign={({ role, accountId }) =>
                setAccounts((current) =>
                    assignRole({ accounts: current, role, accountId }),
                )
            }
            onCreateAccount={(values) =>
                setAccounts((current) =>
                    addCreatedRoleAccount({ accounts: current, values }),
                )
            }
            onClose={() => {
                if (!createTemplate.isPending) {
                    onClose();
                }
            }}
            onConfirm={handleContinue}
        />
    );
};
